import { FC, useCallback, useEffect, useMemo } from 'react';
import { useOrganizationFilterBinding } from 'components/ObjectTable/useOrganizationOptions';
import { useSavedTableFilters } from 'hooks/useSavedTableFilters';
import { isEmpty, isUndefined, omitBy } from 'lodash';
import {
  appPermissionsSelector,
  fetchPermissions,
  scopesSelector,
} from 'slices/permission.slice';
import { fetchRoles, rolesSelector } from 'slices/roles.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TAffiliationFilters } from '@timeedit/types';
import {
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';

type AffiliationFilter = { searchText?: string; filters: TAffiliationFilters };

type Props = {
  filter: AffiliationFilter;
  onFilterChange: (filter: AffiliationFilter) => void;
};

const ORG_FILTER_FIELDS = [
  'standardOrganization',
  'organizationsAndRoles.organizationId',
] as const;

const AffiliationFilter: FC<Props> = (props) => {
  const dispatch = useAppDispatch();

  const scopes = useAppSelector(scopesSelector);
  const permissions = useAppSelector(appPermissionsSelector);
  const roles = useAppSelector(rolesSelector);

  useEffect(() => {
    dispatch(fetchPermissions());
    dispatch(fetchRoles());
  }, []);

  const settings = useMemo(
    () => toFilterSettings(props.filter),
    [props.filter]
  );

  const { savedFilters, onSaveFilter, onDeleteFilter } =
    useSavedTableFilters('affiliations');

  const handleChange = useCallback(
    (s: TEIFilter) => props.onFilterChange(fromFilterSettings(s)),
    [props.onFilterChange]
  );

  const { options: orgOptions, filterProps: orgFilterProps } =
    useOrganizationFilterBinding({
      fields: ORG_FILTER_FIELDS,
      settings,
      onChange: handleChange,
      savedFilters,
      onSaveFilter,
    });

  const options = useMemo(() => {
    const scopeOptions = scopes.map((scopeData) => ({
      value: scopeData.value,
      label: scopeData.value,
    }));

    const permissionOptions = permissions.map((scopeData) => ({
      value: scopeData.value,
      label: scopeData.value,
    }));

    const rolesOptions = roles.map((scopeData) => ({
      value: scopeData.name,
      label: scopeData.name,
    }));

    return {
      standardOrganization: orgOptions,
      'organizationsAndRoles.organizationId': orgOptions,
      'organizationsAndRoles.role': rolesOptions,
      scopes: scopeOptions,
      appPermissions: permissionOptions,
    };
  }, [permissions, roles, scopes, orgOptions]);

  return (
    <Filter
      {...orgFilterProps}
      options={options}
      defaultFields={DEFAULT_FIELDS}
      optionalFields={OPTIONAL_FIELDS}
      onDeleteFilter={onDeleteFilter}
      allowClear
    />
  );
};

export default AffiliationFilter;

const DEFAULT_FIELDS: TEIFilterField[] = [
  {
    key: 'standardOrganization',
    label: 'Standard org.',
    type: TEEFilterType.treeSelect,
    multiselect: false,
  },
  {
    key: 'organizationsAndRoles.organizationId',
    label: 'Organization node',
    type: TEEFilterType.treeSelect,
    multiselect: false,
  },
  {
    key: 'organizationsAndRoles.role',
    label: 'Node role',
    type: TEEFilterType.text,
    multiselect: false,
  },
];

const OPTIONAL_FIELDS: TEIFilterField[] = [
  {
    key: 'label',
    label: 'Description',
    type: TEEFilterType.freeText,
    inputProps: {},
  },
  {
    key: 'matchingCriteria.attribute',
    label: 'Matching criteria attribute',
    type: TEEFilterType.freeText,
    inputProps: {},
  },
  {
    key: 'matchingCriteria.value',
    label: 'Matching criteria value',
    type: TEEFilterType.freeText,
    inputProps: {},
  },
  {
    key: 'scopes',
    label: 'Scopes',
    type: TEEFilterType.text,
    multiselect: true,
  },
  {
    key: 'appPermissions',
    label: 'App permissions.',
    type: TEEFilterType.text,
    multiselect: true,
  },
];

const toFilterSettings = (filter: AffiliationFilter): TEIFilter => ({
  filters: omitBy(
    {
      label: optionalString(filter.filters.label),
      standardOrganization: optionalString(filter.filters.standardOrganization),
      'matchingCriteria.attribute': optionalString(
        filter.filters.matchingCriteria?.attribute
      ),
      'matchingCriteria.value': optionalString(
        filter.filters.matchingCriteria?.value
      ),
      'organizationsAndRoles.organizationId': optionalString(
        filter.filters.organizationsAndRoles?.organizationId
      ),
      'organizationsAndRoles.role': optionalString(
        filter.filters.organizationsAndRoles?.role
      ),
      scopes: optionalStringArray(filter.filters.scopes),
      appPermissions: optionalStringArray(filter.filters.appPermissions),
      userObjects: optionalStringArray(filter.filters.userObjects),
    },
    isUndefined
  ),
  freeTextSearch: optionalString(filter.searchText) || '',
});

const fromFilterSettings = (settings: TEIFilter): AffiliationFilter => ({
  filters: omitBy(
    {
      label: optionalString(settings.filters.label as string),
      standardOrganization: optionalString(
        settings.filters.standardOrganization as string
      ),
      matchingCriteria: optionalObject({
        attribute: optionalString(
          settings.filters['matchingCriteria.attribute'] as string
        ),
        value: optionalString(
          settings.filters['matchingCriteria.value'] as string
        ),
      }),
      organizationsAndRoles: optionalObject({
        organizationId: optionalString(
          settings.filters['organizationsAndRoles.organizationId'] as string
        ),
        role: optionalString(
          settings.filters['organizationsAndRoles.role'] as string
        ),
      }),
      scopes: optionalStringArray(settings.filters.scopes as string | string[]),
      appPermissions: optionalStringArray(
        settings.filters.appPermissions as string | string[]
      ),
      userObjects: optionalStringArray(
        settings.filters.userObjects as string | string[]
      ),
    },
    isUndefined
  ),
  searchText: optionalString(settings.freeTextSearch),
});

const optionalString = (val?: string) => {
  if (val == null || val.trim().length === 0) return undefined;
  return val;
};

const optionalStringArray = (val?: string | string[]) => {
  if (!Array.isArray(val)) return optionalString(val);
  return val?.length > 0 ? val : undefined;
};

const optionalObject = <T extends object>(val: T) => {
  if (val == null) return undefined;
  const reduced = omitBy(val, isUndefined);
  return isEmpty(reduced) ? undefined : reduced;
};
